// app/javascript/controllers/visitors_chart_controller.js

import { Controller } from '@hotwired/stimulus'
import * as d3 from 'd3'

export default class extends Controller {
  static values = {
    chart: Object
  }

  connect () {
    this.renderChart()

    this.resizeObserver = new ResizeObserver(() => {
      this.renderChart()
    })

    this.resizeObserver.observe(this.element)
  }

  disconnect () {
    this.resizeObserver?.disconnect()
    this.removeTooltip()
    d3.select(this.element).selectAll('*').remove()
  }

  renderChart () {
    d3.select(this.element).selectAll('*').remove()
    this.removeTooltip()

    const datasets = this.normalizeDatasets(this.chartValue?.datasets)

    if (datasets.length === 0) return

    const maximumDays =
      d3.max(datasets, (dataset) => dataset.data.length) || 0

    if (maximumDays === 0) return

    const days = d3.range(maximumDays).map((index) => ({
      key: `day-${index + 1}`,
      label: `Day ${index + 1}`,
      index
    }))

    const chartRows = days.map((day) => ({
      ...day,

      values: datasets
        .map((dataset) => ({
          dataset,
          point: dataset.data[day.index]
        }))
        .filter(({ point }) => {
          return point && Number.isFinite(point.y)
        })
    }))

    const width = Math.max(this.element.clientWidth, 320)

    const svg = d3
      .select(this.element)
      .append('svg')
      .attr('width', width)
      .attr('role', 'img')
      .attr('aria-label', 'Visitors grouped bar chart')

    const margin = {
      top: 20,
      right: 24,
      bottom: 44,
      left: 70
    }

    const height = Math.max(
      320,
      margin.top + margin.bottom + 210
    )

    const innerWidth = Math.max(
      width - margin.left - margin.right,
      100
    )

    const innerHeight = Math.max(
      height - margin.top - margin.bottom,
      100
    )

    const allVisitorValues = datasets.flatMap((dataset) =>
      dataset.data.map((point) => point.y)
    )

    const maximumVisitors = d3.max(allVisitorValues) || 0

    svg
      .attr('height', height)
      .attr('viewBox', `0 0 ${width} ${height}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')

    const chart = svg
      .append('g')
      .attr(
        'transform',
        `translate(${margin.left}, ${margin.top})`
      )

    const dayScale = d3
      .scaleBand()
      .domain(days.map((day) => day.key))
      .range([0, innerWidth])
      .paddingInner(0.28)
      .paddingOuter(0.05)

    const datasetScale = d3
      .scaleBand()
      .domain(datasets.map((dataset) => dataset.id))
      .range([0, dayScale.bandwidth()])
      .padding(0.14)

    const yScale = d3
      .scaleLinear()
      .domain([0, maximumVisitors])
      .nice()
      .range([innerHeight, 0])

    this.drawGrid(chart, yScale, innerWidth)
    this.drawXAxis(chart, dayScale, days, innerHeight)
    this.drawYAxis(chart, yScale)

    const crosshair = this.createCrosshair(chart, innerWidth)
    const tooltip = this.createTooltip()

    const dayGroups = chart
      .selectAll('.visitors-chart-day')
      .data(chartRows)
      .join('g')
      .attr('class', 'visitors-chart-day')
      .attr(
        'transform',
        (day) => `translate(${dayScale(day.key)}, 0)`
      )

    const bars = dayGroups
      .selectAll('.visitors-chart-bar')
      .data((day) =>
        day.values.map((value) => ({
          ...value,
          day
        }))
      )
      .join('rect')
      .attr('class', 'visitors-chart-bar')
      .attr('x', ({ dataset }) => datasetScale(dataset.id))
      .attr('width', datasetScale.bandwidth())
      .attr('y', innerHeight)
      .attr('height', 0)
      .attr(
        'fill',
        ({ dataset }) => dataset.backgroundColor
      )
      .attr(
        'stroke',
        ({ dataset }) => dataset.borderColor
      )
      .attr('stroke-width', 0)
      .attr('tabindex', 0)
      .attr('role', 'graphics-symbol')
      .attr('aria-label', ({ dataset, point, day }) => {
        return [
          day.label,
          dataset.label,
          `${d3.format(',')(point.y)} visitors`,
          point.tooltip
        ]
          .filter(Boolean)
          .join(', ')
      })
      .on('mouseenter focus', (event, item) => {
        bars.attr('opacity', (bar) => bar === item ? 1 : 0.25)

        d3.select(event.currentTarget)
          .attr('stroke-width', 2)

        this.showCrosshair(
          crosshair,
          yScale(item.point.y),
          this.formatVisitorCount(item.point.y)
        )
        this.showTooltip(event, item, tooltip)
      })
      .on('mousemove', (event) => {
        this.positionTooltip(event, tooltip)
      })
      .on('mouseleave blur', (event) => {
        bars.attr('opacity', 1)

        d3.select(event.currentTarget)
          .attr('stroke-width', 0)

        crosshair.style('display', 'none')
        tooltip.style('display', 'none')
      })

    bars
      .transition()
      .duration(500)
      .delay((_, index) => index * 25)
      .attr('y', ({ point }) => yScale(point.y))
      .attr(
        'height',
        ({ point }) => innerHeight - yScale(point.y)
      )
  }

  normalizeDatasets (rawDatasets) {
    const defaultColors = [
      '#e60049',
      '#0bb4ff',
      '#50e991',
      '#e6d800',
      '#9b19f5',
      '#ffa300',
      '#dc0ab4',
      '#b3d4ff'
    ]

    if (!Array.isArray(rawDatasets)) return []

    return rawDatasets
      .map((dataset, datasetIndex) => {
        const backgroundColor =
          dataset.backgroundColor ||
          dataset.borderColor ||
          defaultColors[datasetIndex % defaultColors.length]

        return {
          id: `dataset-${datasetIndex + 1}`,

          label: String(
            dataset.label || `Dataset ${datasetIndex + 1}`
          ),

          backgroundColor,

          borderColor:
            dataset.borderColor || backgroundColor,

          data: Array.isArray(dataset.data)
            ? dataset.data
              .map((point) => ({
                tooltip: String(point.tooltip || ''),

                x: point.x
                  ? new Date(point.x)
                  : null,

                y: Number(point.y),

                time: Number(point.time)
              }))
              .filter((point) => Number.isFinite(point.y))
            : []
        }
      })
      .filter((dataset) => dataset.data.length > 0)
  }

  drawGrid (chart, yScale, innerWidth) {
    chart
      .append('g')
      .attr('class', 'visitors-chart-grid')
      .call(
        d3
          .axisLeft(yScale)
          .ticks(8)
          .tickSize(-innerWidth)
          .tickFormat('')
      )
      .call((grid) => grid.select('.domain').remove())
      .call((grid) =>
        grid
          .selectAll('.tick line')
          .attr('stroke', '#e5e7eb')
          .attr('stroke-opacity', 1)
      )
  }

  drawXAxis (chart, dayScale, days, innerHeight) {
    const dayLabels = new Map(
      days.map((day) => [day.key, day.label])
    )

    chart
      .append('g')
      .attr('class', 'visitors-chart-x-axis')
      .attr(
        'transform',
        `translate(0, ${innerHeight})`
      )
      .call(
        d3
          .axisBottom(dayScale)
          .tickSize(0)
          .tickPadding(12)
          .tickFormat((key) => dayLabels.get(key))
      )
      .call((axis) =>
        axis
          .select('.domain')
          .attr('stroke', '#d1d5db')
      )
      .call((axis) =>
        axis
          .selectAll('text')
          .attr('fill', '#111827')
          .style('font-size', '12px')
      )
  }

  drawYAxis (chart, yScale) {
    chart
      .append('g')
      .attr('class', 'visitors-chart-y-axis')
      .call(
        d3
          .axisLeft(yScale)
          .ticks(8)
          .tickSize(0)
          .tickPadding(10)
          .tickFormat(d3.format('~s'))
      )
      .call((axis) => axis.select('.domain').remove())
      .call((axis) =>
        axis
          .selectAll('text')
          .attr('fill', '#6b7280')
          .style('font-size', '11px')
      )
  }

  createCrosshair (chart, innerWidth) {
    const crosshair = chart
      .append('g')
      .attr('class', 'visitors-chart-crosshair')
      .style('display', 'none')
      .style('pointer-events', 'none')

    crosshair
      .append('line')
      .attr('x1', 0)
      .attr('x2', innerWidth)
      .attr('stroke', '#6b7280')
      .attr('stroke-width', 1)
      .attr('stroke-dasharray', '5 5')

    crosshair
      .append('rect')
      .attr('class', 'visitors-chart-crosshair-label-background')
      .attr('x', -62)
      .attr('y', -10)
      .attr('width', 56)
      .attr('height', 20)
      .attr('rx', 2)
      .attr('fill', '#202124')

    crosshair
      .append('text')
      .attr('class', 'visitors-chart-crosshair-label')
      .attr('x', -34)
      .attr('y', 4)
      .attr('fill', '#ffffff')
      .attr('text-anchor', 'middle')
      .style('font-size', '11px')
      .style('font-weight', 600)

    return crosshair
  }

  showCrosshair (crosshair, y, value) {
    crosshair
      .attr('transform', `translate(0, ${y})`)
      .style('display', null)

    crosshair
      .select('.visitors-chart-crosshair-label')
      .text(value)
  }

  formatVisitorCount (value) {
    return d3.format(',')(Math.round(value))
  }

  createTooltip () {
    return d3
      .select('body')
      .append('div')
      .attr('class', 'visitors-chart-tooltip')
      .style('display', 'none')
  }

  showTooltip (event, item, tooltip) {
    const dateText = this.formatDate(item.point.x)

    tooltip
      .style('display', 'block')
      .html(`
        <div class="visitors-chart-tooltip__day">
          ${this.escapeHtml(item.day.label)}
        </div>

        <div class="visitors-chart-tooltip__title">
          <span
            class="visitors-chart-tooltip__legend-swatch"
            style="background-color: ${this.escapeHtml(item.dataset.backgroundColor)}"
          ></span>
          <span>${this.escapeHtml(item.dataset.label)}</span>
        </div>

        ${
          dateText
            ? `<div>${this.escapeHtml(dateText)}</div>`
            : ''
        }

        <div>
          Visitors:
          <strong>${d3.format(',')(item.point.y)}</strong>
        </div>
      `)

    this.positionTooltip(event, tooltip)
  }

  positionTooltip (event, tooltip) {
    const tooltipNode = tooltip.node()

    if (!tooltipNode) return

    const offset = 14
    const tooltipWidth = tooltipNode.offsetWidth
    const tooltipHeight = tooltipNode.offsetHeight

    let left = event.clientX + offset
    let top = event.clientY - tooltipHeight - offset

    if (left + tooltipWidth > window.innerWidth) {
      left = event.clientX - tooltipWidth - offset
    }

    if (top < 0) {
      top = event.clientY + offset
    }

    tooltip
      .style('left', `${left}px`)
      .style('top', `${top}px`)
  }

  formatDate (date) {
    if (
      !(date instanceof Date) ||
      Number.isNaN(date.getTime())
    ) {
      return ''
    }

    return new Intl.DateTimeFormat('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    }).format(date)
  }

  removeTooltip () {
    d3.select('body')
      .selectAll('.visitors-chart-tooltip')
      .remove()
  }

  escapeHtml (value) {
    const element = document.createElement('div')
    element.textContent = String(value)
    return element.innerHTML
  }
}
